// FILE: src/hooks/useStorylineBridge.js
// Listens to zun-bridge v2, which the API injects into every Storyline
// story.html (zun-lms-backend src/content/zun-bridge.js). Interim until the
// SCORM player (plan WP-4).
//
// Since WP-3 it only *reports* what the deck says — which slide is on screen,
// that the last slide was reached, the quiz score. It no longer decides what
// earns credit: the server measures time per slide from heartbeats and
// decides completion (minimum dwell, D5).
import { useCallback, useEffect, useRef, useState } from "react";
import { markActivity } from "../utils/activity";

export default function useStorylineBridge({
  iframeRef,
  resourceSlides = [],
  onReady,
  onLocation,
  onReachedEnd,
  onScore
}) {
  const [deck, setDeck] = useState(null); // { total, mismatch }
  const [current, setCurrent] = useState(null);
  const [error, setError] = useState(null);

  const totalRef = useRef(0);
  const byIdRef = useRef({});
  const lastScoreRef = useRef(null);
  const currentIdRef = useRef(null);

  // Keep the latest callbacks without re-subscribing the listener.
  const cb = useRef({ onReady, onLocation, onReachedEnd, onScore });
  cb.current = { onReady, onLocation, onReachedEnd, onScore };
  const slidesRef = useRef(resourceSlides);
  slidesRef.current = resourceSlides;

  const hello = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (win) win.postMessage({ type: "ZUN_HELLO" }, window.location.origin);
  }, [iframeRef]);

  // Messages from the deck: same origin, from our iframe only.
  useEffect(() => {
    function onMessage(e) {
      if (e.origin !== window.location.origin) return;
      if (!iframeRef.current || e.source !== iframeRef.current.contentWindow) return;
      const d = e.data || {};
      // Anything the deck reports means the student is working in it (B-16).
      markActivity();

      if (d.type === "ZUN_READY") {
        byIdRef.current = Object.fromEntries((d.slides || []).map((s) => [s.id, s]));
        totalRef.current = d.total;
        const expected = slidesRef.current.length;
        setDeck({ total: d.total, mismatch: expected > 0 && expected !== d.total });
        setError(null);
        cb.current.onReady?.(d.total);
      } else if (d.type === "ZUN_SLIDE") {
        const next = byIdRef.current[d.id] || d;
        if (currentIdRef.current === next.id) return;
        currentIdRef.current = next.id;
        setCurrent(next);
        // Results slides have no index: time there belongs to no slide.
        if (next.index != null) cb.current.onLocation?.(next.index);
        if (next.index != null && next.index === totalRef.current) cb.current.onReachedEnd?.();
      } else if (d.type === "ZUN_RESULT") {
        if (lastScoreRef.current !== d.percent) {
          lastScoreRef.current = d.percent;
          cb.current.onScore?.(d.percent);
        }
      } else if (d.type === "ZUN_ERROR") {
        setError(d.reason || "BRIDGE_ERROR");
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef]);

  return { deck, current, error, onIframeLoad: hello };
}
